import { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useQueryClient } from "@tanstack/react-query";
import type { ToastPosition } from "react-toastify";
import { Button, Typography, useBreakpoint } from "@avenue-8/sidekick-ui";
import {
  getContactQueryKey,
  listContactsQueryKey,
  listSuggestionsQueryKey,
  reviewSuggestion,
  type ReviewSocialProfileSuggestionDto,
  type SocialProfileSuggestionWithContactResponseDto,
} from "@shared/generated/crm";
import { isFetchClientError } from "@shared/apis/fetch-clients/fetch-client-error";
import { toast } from "@shared/services/toast/toast";
import { deriveContactName } from "@features/Contacts/contacts-helpers";
import { PLATFORM_LABELS } from "./profile-suggestion-helpers";

export const UNDO_WINDOW_MS = 5000;

const ALREADY_HANDLED_MESSAGES: Record<number, string> = {
  404: "This profile or its contact no longer exists.",
  409: "This profile was already reviewed.",
};

type ReviewStatus = ReviewSocialProfileSuggestionDto["status"];

type PendingReview = {
  suggestion: SocialProfileSuggestionWithContactResponseDto;
  status: ReviewStatus;
  timeoutId: ReturnType<typeof setTimeout>;
  toastId: string;
};

const getContactName = (
  suggestion: SocialProfileSuggestionWithContactResponseDto,
) => deriveContactName(suggestion.contact) || "this contact";

const getDecisionMessage = (
  suggestion: SocialProfileSuggestionWithContactResponseDto,
  status: ReviewStatus,
) => {
  const platformLabel = PLATFORM_LABELS[suggestion.platform];
  const contactName = <strong>{getContactName(suggestion)}</strong>;
  return status === "accepted" ? (
    <>
      {platformLabel} profile confirmed for {contactName}.
    </>
  ) : (
    <>
      {platformLabel} profile marked as not a match for {contactName}.
    </>
  );
};

export const useProfileSuggestionReview = () => {
  const queryClient = useQueryClient();
  const [hiddenSuggestionIds, setHiddenSuggestionIds] = useState<Set<string>>(
    new Set(),
  );
  const pendingReviewsRef = useRef(new Map<string, PendingReview>());
  const decisionCountRef = useRef(0);
  const isMobile = useBreakpoint("mobile");
  const toastPosition: ToastPosition = isMobile
    ? "bottom-center"
    : "bottom-right";

  const showSuggestion = useCallback((suggestionId: string) => {
    setHiddenSuggestionIds((prev) => {
      const next = new Set(prev);
      next.delete(suggestionId);
      return next;
    });
  }, []);

  const commitReview = useCallback(
    async (
      suggestion: SocialProfileSuggestionWithContactResponseDto,
      status: ReviewStatus,
    ) => {
      try {
        await reviewSuggestion(suggestion.id, { status }, undefined, {
          keepalive: true,
        });
        if (status === "accepted") {
          queryClient.invalidateQueries({
            queryKey: getContactQueryKey(suggestion.contactId),
          });
          queryClient.invalidateQueries({ queryKey: listContactsQueryKey() });
        }
      } catch (error) {
        const alreadyHandledMessage = isFetchClientError(error)
          ? ALREADY_HANDLED_MESSAGES[error.status]
          : undefined;
        if (alreadyHandledMessage) {
          toast.info(alreadyHandledMessage, { position: toastPosition });
        } else {
          showSuggestion(suggestion.id);
          toast.error(
            `Couldn't save your review for ${getContactName(suggestion)}. Please try again.`,
            { position: toastPosition },
          );
        }
      } finally {
        queryClient.invalidateQueries({ queryKey: listSuggestionsQueryKey() });
      }
    },
    [queryClient, showSuggestion, toastPosition],
  );

  const commitReviewRef = useRef(commitReview);
  useEffect(() => {
    commitReviewRef.current = commitReview;
  }, [commitReview]);

  const flushReview = useCallback((suggestionId: string) => {
    const pendingReview = pendingReviewsRef.current.get(suggestionId);
    if (!pendingReview) return;
    clearTimeout(pendingReview.timeoutId);
    pendingReviewsRef.current.delete(suggestionId);
    toast.dismiss(pendingReview.toastId);
    void commitReviewRef.current(
      pendingReview.suggestion,
      pendingReview.status,
    );
  }, []);

  const undoReview = useCallback(
    (suggestionId: string) => {
      const pendingReview = pendingReviewsRef.current.get(suggestionId);
      if (!pendingReview) return;
      clearTimeout(pendingReview.timeoutId);
      pendingReviewsRef.current.delete(suggestionId);
      toast.dismiss(pendingReview.toastId);
      showSuggestion(suggestionId);
    },
    [showSuggestion],
  );

  const reviewProfileSuggestion = useCallback(
    (
      suggestion: SocialProfileSuggestionWithContactResponseDto,
      status: ReviewStatus,
    ) => {
      if (pendingReviewsRef.current.has(suggestion.id)) return;
      setHiddenSuggestionIds((prev) => new Set(prev).add(suggestion.id));

      decisionCountRef.current += 1;
      const toastId = `profile-review-undo-${suggestion.id}-${decisionCountRef.current}`;
      const timeoutId = setTimeout(
        () => flushReview(suggestion.id),
        UNDO_WINDOW_MS,
      );
      pendingReviewsRef.current.set(suggestion.id, {
        suggestion,
        status,
        timeoutId,
        toastId,
      });

      toast(
        <ToastContent>
          <Typography>{getDecisionMessage(suggestion, status)}</Typography>
          <UndoButton
            variant="text"
            size="small"
            onClick={() => undoReview(suggestion.id)}
          >
            Undo
          </UndoButton>
        </ToastContent>,
        {
          toastId,
          position: toastPosition,
          autoClose: UNDO_WINDOW_MS,
          pauseOnHover: false,
          pauseOnFocusLoss: false,
          closeOnClick: false,
          draggable: false,
        },
      );
    },
    [flushReview, undoReview, toastPosition],
  );

  useEffect(() => {
    const pendingReviews = pendingReviewsRef.current;
    const flushAllReviews = () =>
      [...pendingReviews.keys()].forEach(flushReview);

    window.addEventListener("pagehide", flushAllReviews);
    return () => {
      window.removeEventListener("pagehide", flushAllReviews);
      flushAllReviews();
    };
  }, [flushReview]);

  return { hiddenSuggestionIds, reviewProfileSuggestion };
};

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const UndoButton = styled(Button)`
  flex-shrink: 0;
  min-width: 0;
  padding: 0.25rem 0.75rem;
  font-size: ${(p) => p.theme.text.fontSizes.s};
`;
